// ═══════════════════════════════════════════════════════════════════
//  modules/chat/SujPanel.js
//  SujPanel
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../../socle/api";
import { useIsMobile } from "../../socle/hooks";
import { THEME } from "../../socle/theme";
import { SUJ_CANAUX } from "./constantes";
import { SujRunsModal } from "./SujRunsModal";
import { SujActionsBas } from "./SujActionsBas";
import { SujAlerteColonne } from "./SujAlerteColonne";
import { SujCompteurs } from "./SujCompteurs";
import { SujEtatSysteme } from "./SujEtatSysteme";
import { SujFiltres } from "./SujFiltres";
import { SujRecaps } from "./SujRecaps";
import { SujRegistre } from "./SujRegistre";
import { SujVieServeur } from "./SujVieServeur";

// ═══════════════ PANNEAU D'UN CANAL ════════════════════════════════════
export function SujPanel({ token, user, canal, onCanaux }) {
  const isMobile = useIsMobile();
  const canRun = !!user && (user.role === "super_admin" || user.role === "admin");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [running, setRunning] = useState(false);
  const [recapRunning, setRecapRunning] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [showRuns, setShowRuns] = useState(false);
  const [selected, setSelected] = useState([]);

  const [statut, setStatut] = useState("ouvert");
  const [categorie, setCategorie] = useState("");
  const [sort, setSort] = useState("severite");
  const [occMin, setOccMin] = useState(1);
  const [since, setSince] = useState("");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [recapJour, setRecapJour] = useState("");
  const [page, setPage] = useState(0);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const p = new URLSearchParams({ canal, statut, sort, occ_min: String(occMin), limit: String(LIMIT), offset: String(page * LIMIT) });
      if (categorie) p.set("categorie", categorie);
      if (since) p.set("since", since);
      if (q) p.set("q", q);
      if (recapJour) p.set("recap_jour", recapJour);
      const res = await fetch(`${API_BASE}/api/chat/sujets?${p}`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (!res.ok) { setError(json.error || "Erreur de chargement"); setData(null); }
      else { setData(json); if (json.canaux && onCanaux) onCanaux(json.canaux); }
    } catch { setError("Impossible de contacter le serveur"); }
    setLoading(false);
  }, [LIMIT, canal, categorie, occMin, onCanaux, page, q, recapJour, since, sort, statut, token]);

  useEffect(() => { setPage(0); setSelected([]); }, [canal, statut, categorie, sort, occMin, since, q]);
  useEffect(() => { load(); }, [canal, statut, categorie, sort, occMin, since, q, page, recapJour, load]);

  const setStatutSujet = async (id, nouveauStatut) => {
    setBusyId(id);
    try {
      const res = await fetch(`${API_BASE}/api/chat/sujets/${id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ statut: nouveauStatut }),
      });
      if (!res.ok) { const j = await res.json(); setError(j.error || "Modification impossible"); }
      else await load();
    } catch { setError("Impossible de contacter le serveur"); }
    setBusyId(null);
  };

  const bulk = async (nouveauStatut) => {
    if (selected.length === 0) return;
    try {
      const res = await fetch(`${API_BASE}/api/chat/sujets`, {
        method: "PATCH", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: selected, statut: nouveauStatut }),
      });
      const j = await res.json();
      if (!res.ok) setError(j.error || "Action impossible");
      else { setNotice(`${j.modifies} sujet(s) mis à jour.`); setSelected([]); await load(); }
    } catch { setError("Impossible de contacter le serveur"); }
  };

  const forcerTache = async (sujet) => {
    setBusyId(sujet.id); setError(""); setNotice("");
    try {
      const res = await fetch(`${API_BASE}/api/chat/sujets/${sujet.id}/tache`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const j = await res.json();
      if (!res.ok) setError(j.error + (j.alter_sql ? ` — SQL à exécuter : ${j.alter_sql}` : ""));
      else { setNotice(`Tâche #${j.task_id} ${j.etat === "creee" ? "créée" : "mise à jour"} dans le projet « ${j.project_nom} ».`); await load(); }
    } catch { setError("Impossible de contacter le serveur"); }
    setBusyId(null);
  };

  const runNow = async () => {
    setRunning(true); setNotice(""); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/chat/analysis/run`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ canal }),
      });
      const j = await res.json();
      if (!res.ok) setError(j.error || "Échec de l'analyse");
      else {
        const r = (j.resultats || {})[canal] || {};
        setNotice(r.skipped === "no_messages"
          ? `Aucun message sur ce canal pour la fenêtre du ${j.window_start} — rien à analyser.`
          : r.error ? `Erreur : ${r.error}`
          : `Analyse terminée : ${r.nouveaux || 0} nouveau(x) sujet(s), ${r.maj || 0} mis à jour, ${r.taches_creees || 0} tâche(s) créée(s), ${r.taches_majs || 0} tâche(s) mise(s) à jour.`);
        await load();
      }
    } catch { setError("Impossible de contacter le serveur"); }
    setRunning(false);
  };

  const runRecaps = async () => {
    setRecapRunning(true); setNotice(""); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/chat/recaps/run`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ canal, jour: recapJour || undefined }),
      });
      const j = await res.json();
      if (!res.ok) setError(j.error || "Échec de la génération");
      else { setNotice(`Récaps régénérés — quotidien : ${j.quotidien}, global : ${j.global}.`); await load(); }
    } catch { setError("Impossible de contacter le serveur"); }
    setRecapRunning(false);
  };

  const etatCanal = data && (data.canaux || []).find((x) => x.id === canal);
  const vide = etatCanal && !etatCanal.alimente && (!data.sujets || data.sujets.length === 0);

  if (loading && !data) return <div style={{ color: THEME.textMuted, padding: 20 }}>Chargement du registre…</div>;

  if (data && data.unavailable) return (
    <div style={{ background: THEME.amberDim, border: `1px solid ${THEME.amber}`, borderRadius: 8, padding: "12px 16px", color: THEME.amber, fontSize: 13 }}>
      ⏳ La table de messages du chat n'existe pas encore — les analyses démarreront dès les premiers messages reçus du plugin.
    </div>
  );

  if (vide) return (
    <div style={{ background: THEME.panel, border: `1px dashed ${THEME.border}`, borderRadius: 14, padding: 40, textAlign: "center" }}>
      <div style={{ fontSize: 34, marginBottom: 10 }}>{(SUJ_CANAUX.find((x) => x.id === canal) || {}).icon}</div>
      <div style={{ color: THEME.text, fontSize: 15, fontWeight: 700 }}>Ce canal n'est pas encore alimenté</div>
      {canal === "discord" ? (
        <div style={{ color: THEME.textMuted, fontSize: 13, marginTop: 8, lineHeight: 1.55, maxWidth: 520, margin: "8px auto 0" }}>
          Noctaviel n'envoie pas encore les messages de <b>#taverne</b> et <b>#suggestion</b> vers
          <code style={{ color: THEME.gold }}> /api/chat/ingest</code>.<br />
          Il faut installer <code style={{ color: THEME.gold }}>chat-ingest.js</code> dans le bot et renseigner
          <code style={{ color: THEME.gold }}> INGEST_CHANNELS</code> — voir la notice « Global Discord ».
        </div>
      ) : (
        <div style={{ color: THEME.textMuted, fontSize: 13, marginTop: 8, lineHeight: 1.55, maxWidth: 480, margin: "8px auto 0" }}>
          Le plugin n'envoie pas encore de messages avec <code style={{ color: THEME.gold }}>channel={canal === "mp" ? '"mp"' : '"boxchat"'}</code> sur
          <code style={{ color: THEME.gold }}> /api/chat/ingest</code>.<br />
          Rien à faire de ton côté : dès que Brice enverra ce canal, l'analyse démarre au passage suivant.
        </div>
      )}
    </div>
  );

  const cpt = (data && data.compteurs) || null;
  const sys = (data && data.systeme) || {};
  const tachesCfg = sys.taches || {};
  const runs = (data && data.derniers_passages) || [];
  const dernier = runs[0];
  const rg = data && data.recap_global;
  const rq = data && data.recap_quotidien;
  const vieServeur = (data && data.vie_serveur) || [];
  const joursRecap = (data && data.recap_jours) || [];
  const totalPages = data ? Math.ceil(data.total / LIMIT) : 1;
  const maxOcc = cpt ? Math.max(1, cpt.max_occurrences) : 1;
  const allSelected = data && data.sujets.length > 0 && selected.length === data.sujets.length;

  const carte = (bordure) => ({ background: THEME.panel, border: `1px solid ${bordure || THEME.border}`, borderRadius: 12, padding: 16 });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      <SujAlerteColonne tachesCfg={tachesCfg} />

      {notice && <div style={{ background: THEME.amberDim, border: `1px solid ${THEME.amber}`, color: THEME.amber, borderRadius: 8, padding: "9px 14px", fontSize: 12 }}>{notice}</div>}
      {error && <div style={{ background: THEME.redDim, border: `1px solid ${THEME.red}`, color: THEME.red, borderRadius: 8, padding: "9px 14px", fontSize: 12 }}>{error}</div>}

      <SujRecaps canRun={canRun} carte={carte} isMobile={isMobile} joursRecap={joursRecap} recapJour={recapJour} rg={rg} rq={rq} setRecapJour={setRecapJour} />

      <SujVieServeur carte={carte} isMobile={isMobile} vieServeur={vieServeur} />

      <SujCompteurs categorie={categorie} cpt={cpt} setCategorie={setCategorie} statut={statut} />

      <SujEtatSysteme carte={carte} dernier={dernier} sys={sys} tachesCfg={tachesCfg} />

      <SujFiltres categorie={categorie} data={data} maxOcc={maxOcc} occMin={occMin} qInput={qInput} setCategorie={setCategorie} setOccMin={setOccMin} setQ={setQ} setQInput={setQInput} setSince={setSince} setSort={setSort} setStatut={setStatut} since={since} sort={sort} statut={statut} />

      <SujRegistre LIMIT={LIMIT} allSelected={allSelected} bulk={bulk} busyId={busyId} data={data} forcerTache={forcerTache} isMobile={isMobile} loading={loading} page={page} selected={selected} setPage={setPage} setSelected={setSelected} setStatutSujet={setStatutSujet} token={token} totalPages={totalPages} />

      <SujActionsBas canRun={canRun} recapRunning={recapRunning} runNow={runNow} runRecaps={runRecaps} running={running} setShowRuns={setShowRuns} sys={sys} />

      {showRuns && <SujRunsModal token={token} canal={canal} onClose={() => setShowRuns(false)} />}
    </div>
  );
}
